import { debounce } from '../debounce.js';
import { isSameDOMRect } from '../dom/rect.js';
import { createResizeObservable } from '../observable/create-resize-observable.js';
import { Observable } from '../observable/observable.js';

export interface DOMRectObservableOptions {
    events: Array<keyof WindowEventMap>;
    debounceTime?: number;
}

/**
 * Observe changes to HTMLElement through browser events and when element dimension change
 */
export const createDOMRectObservable = (element: HTMLElement, { events, debounceTime = 100 }: DOMRectObservableOptions) => {
    return new Observable<DOMRect>(subscriber => {
        const { signal } = subscriber;

        let lastRect = new DOMRect(-1, -1, -1, -1);

        // limit cpu workload. resize & events might happen at the same time & getBoundingClintRect is costly.
        const update = debounce(() => {
            const currentRect = element.getBoundingClientRect();
            if (!isSameDOMRect(currentRect, lastRect)) subscriber.next(currentRect);
        }, debounceTime);

        // gather dimension changes to element - don't need ResizeObserverEntries, just the trigger that element has changed
        createResizeObservable(element).subscribe(update, { signal });

        // element position might change when user interacts with site
        for (const eventName of events) {
            window.addEventListener(eventName, update, { signal });
        }
    });
};
